// Membership tier configuration with Stripe price IDs
export type MembershipTier = 'explorer' | 'connector' | 'catalyst';
export type BillingPeriod = 'monthly' | 'annual';

export interface TierConfig {
  name: string;
  description: string;
  maxListings: number;
  monthlyServiceRequests: number;
  weeklyAdviceLimit: number;
  canRequestIntroductions: boolean;
  canAccessPrivateGroups: boolean;
  canHostEvents: boolean;
  priorityMatching: boolean;
  humanCuratedMatches: boolean;
  featuredVisibility: boolean;
}

export interface PriceConfig {
  priceId: string;
  productId: string;
  amount: number; // in cents
  period: BillingPeriod;
}

// Stripe product/price mapping (LIVE MODE)
export const MEMBERSHIP_PRICES: Record<MembershipTier, Record<BillingPeriod, PriceConfig | null>> = {
  explorer: {
    monthly: null, // Free tier
    annual: null,
  },
  connector: {
    monthly: {
      priceId: 'price_1T0yEMCOlY2NtfOVzu3vo97c',
      productId: 'prod_Tyw6zxmqoTX8Mr',
      amount: 900,
      period: 'monthly',
    },
    annual: {
      priceId: 'price_1T0yEbCOlY2NtfOVEN4ymbAt',
      productId: 'prod_Tyw6WkJv6bs47m',
      amount: 9000,
      period: 'annual',
    },
  },
  catalyst: {
    monthly: {
      priceId: 'price_1T0yEoCOlY2NtfOVDxr9FlvV',
      productId: 'prod_Tyw7wOia9WdwAh',
      amount: 2900,
      period: 'monthly',
    },
    annual: {
      priceId: 'price_1T0yF0COlY2NtfOVbJnlFJoA',
      productId: 'prod_Tyw7wx5Lv3yjLt',
      amount: 29000,
      period: 'annual',
    },
  },
};

// Tier capabilities configuration
export const TIER_CONFIG: Record<MembershipTier, TierConfig> = {
  explorer: {
    name: 'Explorer',
    description: 'Discover the ecosystem and find your place',
    maxListings: 1,
    monthlyServiceRequests: 2,
    weeklyAdviceLimit: 2,
    canRequestIntroductions: true,
    canAccessPrivateGroups: false,
    canHostEvents: false,
    priorityMatching: false,
    humanCuratedMatches: false,
    featuredVisibility: false,
  },
  connector: {
    name: 'Connector',
    description: 'Full access to intentional matching',
    maxListings: 3,
    monthlyServiceRequests: 5,
    weeklyAdviceLimit: 10,
    canRequestIntroductions: true,
    canAccessPrivateGroups: true,
    canHostEvents: false,
    priorityMatching: false,
    humanCuratedMatches: false,
    featuredVisibility: false,
  },
  catalyst: {
    name: 'Catalyst',
    description: 'Lead and shape the ecosystem',
    maxListings: 10,
    monthlyServiceRequests: 999999,
    weeklyAdviceLimit: 999999,
    canRequestIntroductions: true,
    canAccessPrivateGroups: true,
    canHostEvents: true,
    priorityMatching: true,
    humanCuratedMatches: true,
    featuredVisibility: true,
  },
};

// Helper to format price for display
export const formatPrice = (amount: number, period: BillingPeriod): string => {
  const dollars = amount / 100;
  if (period === 'annual') {
    return `$${dollars}/year`;
  }
  return `$${dollars}/month`;
};

// Helper to calculate monthly equivalent for annual plans
export const getMonthlyEquivalent = (amount: number): string => {
  const monthly = Math.round(amount / 12) / 100;
  return `$${monthly.toFixed(0)}/month`;
};
